import React, { useContext, useState, useMemo, useEffect } from 'react';
import _ from 'lodash';
import Dropzone from 'react-dropzone';
import { useDropzone } from 'react-dropzone';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { gql, useMutation, useLazyQuery } from '@apollo/client';
import {
  Box,
  CircularProgress,
  AccordionSummary,
  AccordionDetails,
  Accordion,
  Grid,
  Button,
  List,
  ListItem,
  IconButton,
  ListItemText,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSelector } from 'react-redux';

import { selectProject } from '../../../features/project/projectSlice';
import { selectUser } from '../../../features/auth/authSlice';
import { useAppDispatch } from '../../../app/hooks';
import { showSuccessSnackbar } from '../../../features/snackbar/snackbarSlice';
import { accessAllowed } from '../../../features/project/projectHooks';
import { useCreateActivity } from '../../../features/activity/activityHooks';
import { UnitContext } from '../../../context/UnitContext';
import TextInput from '../../common/formControls/TextInput';
import Dropdown from '../../common/formControls/Dropdown';
import { IDocuments } from '../../../types/docusign';
import { mergeType } from '../../../utils/Constants';
import { IMerge } from '../../../types/merge';
import { IExecutor } from '../../../types/project';
import {
  createPdf,
  convertAllDates,
  setBackgroundColor,
  getMergeValues,
  wrapText,
  drawCheckbox,
} from '../../../utils/Functions';
import CustomDialog from '../../common/CustomDialog';
import LoadingWrapper from '../../common/LoadingWrapper';
import PdfCard from '../../common/PdfCard';
import { FlexBetween, FlexEnd } from '../../../commonStyles';
import { LeaseContext } from '../../../context/LeaseContext';
import { baseStyle, activeStyle, acceptStyle, rejectStyle } from '../../../css/dropzone';
import { IPdfTemplate, IDocumentData, ISigner } from '../../../types/boldSign';
import { IDealRealtor, IPurchaserInfo } from '../../../types/CreateDealForm';

const Documents = () => {
  const { unit, filteredDeal } = useContext(UnitContext);
  const { lease, setLease, updateLease, updateUnit, mergeTemplates, updateListing } = useContext(LeaseContext);
  const createActivity = useCreateActivity();
  const storeDispatch = useAppDispatch();
  const project = useSelector(selectProject);
  const user = useSelector(selectUser);

  const [executor, setExecutor] = useState<IExecutor | null>(project.executors ? project.executors[0] : null);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [dialogType, setDialogType] = useState<string>('');
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [selectedMerge, setSelectedMerge] = useState<IMerge[]>([]);
  const [fileImage, setFileImage] = useState<any>('');
  const [file, setFile] = useState<any>(null);
  const [pdfTemplate, setPdfTemplate] = useState<IPdfTemplate | null>(null);
  const [documentData, setDocumentData] = useState<IDocumentData | null>(null);
  const [templateLoading, setTemplateLoading] = useState<boolean>(false);
  // Upload
  const [title, setTitle] = useState<string>('');
  const [documentType, setDocumentType] = useState<string>('');

  const { isDragActive, isDragAccept, isDragReject } = useDropzone({
    accept: 'image/jpeg, image/png',
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  useEffect(() => {
    if (pdfTemplate) {
      const receiveMessage = (event: any) => {
        if (event.origin !== 'https://app-ca.boldsign.com') {
          return;
        } else {
          if (event.data.status === 'onCreateSuccess' && event.data.action === 'TemplateCreate') {
            window.removeEventListener("message", receiveMessage, false);
            setPdfTemplate(null);
            reviewDocument(true, null);
          }
        }
      }
  
      window.addEventListener("message", receiveMessage, false);
  
      return () => {
        window.removeEventListener("message", receiveMessage, false);
      }
    }
  }, [pdfTemplate]);
  
  const createTemplate = async () => {
    setTemplateLoading(true);
    let iframeEle: any = document.getElementById('prepare_page');
    function onCreateClick() {
      iframeEle?.contentWindow.postMessage('onCreateClick', '*');
    }
    await onCreateClick();
  };

  const [savePdf, { loading: reviewLoading }] = useMutation(SAVEPDF, {
      onCompleted: (data) => {
        setPdfTemplate(data.savePdf);
      },
      onError: (err) => {
        console.log(err, 'err');
      },
    });
  

  const [deleteDocument, { loading: deleteDocumentLoading }] = useMutation(DELETEDOCUMENT, {
    onCompleted: (data) => {
      if (data.documentDeleteById.name.toLowerCase().includes('amendment to listing agreement')) {
        updateListing({
          variables: {
            _id: lease.listing._id,
            record: {
              pendingAmount: null,
              pendingExpiryDate: null,
            },
          },
        });
      }
      let removedDocument = lease.documents.filter((document: IDocuments) => {
        return data.documentDeleteById._id !== document._id;
      });
      setLease({
        ...lease,
        documents: removedDocument,
      });
      updateLease({
        variables: {
          _id: lease._id,
          record: {
            documents: removedDocument.map((document: IDocuments) => document._id),
          },
        },
      });
      storeDispatch(showSuccessSnackbar(`Document has been deleted/voided!`));
      createActivity({
        project: project._id,
        user: user._id,
        deal: null,
        title: `Lease Unit ${unit.suite} Delete Document`,
        content: `Document ${data.documentDeleteById.name} has been deleted and void. The status was ${data.documentDeleteById.status}`,
      });
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [createDocument, { loading: createDocumentLoading }] = useMutation(CREATEDOCUMENT, {
    onCompleted: (data) => {
      setLease({
        ...lease,
        documents: [...lease.documents, data.documentCreateOne],
      });
      let documents = lease.documents;
      if (lease.documents.length) {
        documents = [...lease.documents.map((document: IDocuments) => document._id), data.documentCreateOne._id];
      } else {
        documents = [data.documentCreateOne._id];
      }
      if (documentType === 'Tenancy') {
        updateUnit({ variables: { _id: lease.unit._id, record: { leaseStatus: 'LC' } } });
      }
      if (documentType === 'Listing') {
        updateUnit({ variables: { _id: lease.unit._id, record: { leaseStatus: 'LIST' } } });
      }
      updateLease({
        variables: {
          _id: lease._id,
          record: {
            documents,
          },
        },
      });
      setSelectedMerge([]);
      storeDispatch(showSuccessSnackbar(`Document has been created!`));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [createMultipleDocument, { loading: loading }] = useMutation(CREATEMULTIPLEDOCUMENT, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar(`Document has been sent!`));
      setLease({
        ...lease,
        documents: [...lease.documents, data.createEnvelopeMultipleDocuments],
      });
      let documents = lease.documents;
      if (lease.documents.length) {
        documents = [...lease.documents.map((document: IDocuments) => document._id), data.documentCreateOne._id];
      } else {
        documents = [data.documentCreateOne._id];
      }
      updateLease({
        variables: { _id: lease._id, record: { documents: [...documents, data.createEnvelopeMultipleDocuments._id] } },
      });
      setTitle('');
      setDocumentType('');
      setFileImage(null);
    },
    onError: (err) => {
      setDialogType('');
      setDialogOpen(false);
      console.log(err, 'err');
    },
  });

  const [sendDocument, { loading: sendDocumentLoading }] = useMutation(SENDDOCUMENT, {
    onCompleted: (data) => {
      const documentsClone = [...lease.documents];
      const updatedDocument = documentsClone.find((doc) => doc._id === data.documentCreateEnvelope._id);
      updatedDocument.status = data.documentCreateEnvelope.status;
      updatedDocument.dsEnvelopeId = data.documentCreateEnvelope.dsEnvelopeId;
      setLease({
        ...lease,
        documents: documentsClone,
      });
      createActivity({
        project: project._id,
        user: user._id,
        deal: null,
        title: `Lease Unit ${unit.suite} Document Sent!`,
        content: `Document ${data.documentCreateEnvelope.name} has been sent!`,
      });
      storeDispatch(showSuccessSnackbar(`Document has been sent!`));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [getDocument, { loading: previewLoading }] = useLazyQuery(GETDOCUMENT, {
      fetchPolicy: 'no-cache',
      onCompleted: (data) => {
        setDocumentData(data.getDocument);
        const element = document.createElement('a');
        element.href = data.getDocument.url;
        element.target = '_blank';
        element.click();
      },
      onError: (err) => {
        console.log(err, 'err');
      },
    });
  

  const [resendDocument, { loading: resendLoading }] = useLazyQuery(RESENDDOCUMENT, {
     fetchPolicy: 'no-cache',
     onCompleted: (data) => {
     },
     onError: (err) => {
       console.log(err, 'err');
     },
   });
 

  const [sendDocumentToExecutor, { loading: sendingDocumentToExecutor }] = useMutation(SENDDOCUMENTTOEXECUTOR, {
    onCompleted: (data) => {
      const documentsClone = _.cloneDeep(lease.documents);
      const updatedDocument = documentsClone.find((doc: any) => doc._id === data.documentSendToExecutor._id);
      updatedDocument.status = data.documentSendToExecutor.status;
      createActivity({
        project: project._id,
        user: user._id,
        deal: null,
        title: `Lease Unit ${unit.suite} Document Sent To Executor`,
        content: `Document ${data.documentSendToExecutor.name} has been sent to the Executor ${executor?.name}`,
      });
      storeDispatch(showSuccessSnackbar(`Document has been sent to Executor ${executor?.name}`));
      setExecutor(null);
      setLease({
        ...lease,
        documents: documentsClone,
      });
      setDialogOpen(false);
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  ////////////////////////

  const handleDrop = (acceptedFiles: any) => {
    const file = acceptedFiles[0];
    const fileReader = new FileReader();
    if (file) {
      fileReader.readAsDataURL(file);
    }
    fileReader.onloadend = async () => {
      setFileImage(fileReader.result);
      setFile(acceptedFiles[0]);
    };
  };

  const previewDocument = (doc: any) => {
    getDocument({
      variables: {
        project: project._id,
        document: doc._id,
      },
    });
  };

  const sendToExecutor = () => {
    sendDocumentToExecutor({
      variables: {
        documentId: selectedDocument!._id,
        executor,
      },
    });
    setDialogOpen(false);
  };

  const reviewDocument = async (template: boolean, document: any) => {
      let dealRealtorId = null;
      let selectedRealtor = filteredDeal.realtor.find((realtor: IDealRealtor) => selectedDocument?.name.includes(`${realtor.fullName}`));
  
      if (selectedRealtor && selectedDocument?.type === 'Coop') {
        dealRealtorId = selectedRealtor._id;
      }
  
      // let purchasers = [
      //   ...filteredDeal.purchasers.map((purchaser: IPurchaserInfo) => purchaser._id),
      //   ...filteredDeal.pendingAddPurchasers.map((purchaser: IPurchaserInfo) => purchaser._id),
      //   ...filteredDeal.pendingDeletePurchasers.map((purchaser: IPurchaserInfo) => purchaser._id),

      // ];
      
      if (pdfTemplate) {
        sendDocument({
          variables: {
            documentId: selectedDocument?._id,
            templateId: pdfTemplate.templateId,
            dealRealtorId: dealRealtorId,
          },
        });
      } else {
        if (!template) {
          await savePdf({
            variables: {
              project: project._id,
              unit: filteredDeal.unit._id,
              document: document._id,
              purchaserIds: lease.landlords.map((landlord: IPurchaserInfo) => landlord._id),
              realtor: dealRealtorId,
              mergeTemplate: null,
              floorplan: null,
              additional: document ? document.mergeTemplate : selectedDocument?.mergeTemplate,
              executor: true,
              lease: lease._id,
              aps: false,
            },
          });
        }
      }
    };
  

  const executorList = () => {
    let executorNames = project.executors.map((executor: IExecutor, index: number) => {
      return executor.name;
    });
    return executorNames;
  };

  const handleExecutorDropdown = (event: React.ChangeEvent<{ name?: string; value: string }>) => {
    let executor = project?.executors.find((executor: IExecutor) => executor.name === event.target.value);
    setExecutor(executor!);
  };

  const openDialog = async (type: string, document: IDocuments | null = null) => {
    if (type === 'execute' || type === 'sendEnvelope' || type === 'deleteDocument') {
      setSelectedDocument(document);
    }
    await setDialogType(type);
    await setDialogOpen(true);
  };

  const accordianContent = (document: IDocuments) => {
    if (document.status !== 'Prepared') {
      return (
        <Box
          sx={{
            mb: 2,
            width: '100%',
          }}
        >
          {documentData ? (
            <Box>
              <Box sx={{ mb: 2 }}>
                Document Completed: <strong>{documentData.completedAt ? convertAllDates(documentData.completedAt, 'PPpp') : 'N/A'}</strong>
              </Box>
              {documentData.signers.map((signer: ISigner) => {
                return (
                  <Box sx={{ mb: 2 }}>
                    <strong>{signer.name ? signer.name : 'Executor'}</strong>
                    <Box>
                      <Box>
                        Opened: <strong>{signer.openedAt ? convertAllDates(signer.openedAt, 'PPpp') : 'N/A'}</strong>
                      </Box>
                      <Box>
                        Completed: <strong>{signer.completedAt ? convertAllDates(signer.completedAt, 'PPpp') : 'N/A'}</strong>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : null}
          <FlexBetween sx={{ mt: 2 }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
              }}
            >
              <Button sx={{ mr: 1, fontSize: '0.7rem', '@media (max-width:1499.98px)': { fontSize: '0.56rem' } }} onClick={() => previewDocument(document)} color="primary" variant="contained">
                {previewLoading ? <CircularProgress size={30} color="secondary" /> : 'Preview Document'}
              </Button>
              {document.status !== 'Completed' && document.dsEnvelopeId && document.status !== 'Prepared' ? (
                <Button sx={{ fontSize: '0.7rem', '@media (max-width:1499.98px)': { fontSize: '0.56rem' } }} onClick={() => resendDocument({ variables: { _id: document._id } })} color="primary" variant="contained">
                  {resendLoading ? <CircularProgress size={30} color="secondary" /> : 'Resend Envelope'}
                </Button>
              ) : null}
            </Box>
            <FlexEnd>
              {(document.status === 'Signed' || document.status === 'Verified') && accessAllowed(user, project._id, 'editDeals') ? (
                <Button
                  sx={{ mr: 1, fontSize: '0.7rem', '@media (max-width:1499.98px)': { fontSize: '0.56rem' } }}
                  onClick={() => openDialog('execute', document)}
                  color="success"
                  variant="contained"
                >
                  {sendingDocumentToExecutor ? (
                    <CircularProgress size={24} color="secondary" />
                  ) : (
                    `Send to ${document.status === 'Verified' ? 'Different' : ''} Executor`
                  )}
                </Button>
              ) : null}
              {accessAllowed(user, project._id, 'editDeals') ? (
                <>
                  {!document.dsEnvelopeId ||
                  (document.status === 'Completed' && !document.dsEnvelopeId) ||
                  (!document.isAPS && document.status !== 'Completed') ? (
                    <Button
                      sx={{ fontSize: '0.7rem', '@media (max-width:1499.98px)': { fontSize: '0.56rem' } }}
                      onClick={() => openDialog('deleteDocument', document)}
                      color="error"
                      variant="contained"
                    >
                      {'Void and Delete Document'}
                    </Button>
                  ) : null}
                </>
              ) : null}
            </FlexEnd>
          </FlexBetween>
        </Box>
      );
    } else {
      return (
        <Box
          sx={{
            mb: 2,
            width: '100%',
          }}
        >
          <div>
            <div>This document has not been sent out yet</div>
          </div>
          {pdfTemplate && pdfTemplate.createUrl ? (
            <Box sx={{ height: '768px', width: '100%', mt: 2 }}>
              <iframe
                sandbox="allow-same-origin allow-scripts allow-forms"
                id="prepare_page"
                src={pdfTemplate?.createUrl}
                height="100%"
                width="100%"
                style={{ border: 0, height: '100%' }}
                title="pdfTemplate"
              />
            </Box>
          ) : null}
          <FlexBetween sx={{ mt: 2 }}>
            <div>
              <Button sx={{ fontSize: '0.7rem', '@media (max-width:1499.98px)': { fontSize: '0.56rem' } }} onClick={() => previewDocument(document)} color="primary" variant="contained">
                {previewLoading ? <CircularProgress size={30} color="secondary" /> : 'Preview Document'}
              </Button>
            </div>
            {accessAllowed(user, project._id, 'editDeals') ? (
              <FlexEnd>
                <FlexBetween>
                  <Button
                    sx={{ mr: 1, fontSize: '0.7rem', '@media (max-width:1499.98px)': { fontSize: '0.56rem' } }}
                    onClick={() => openDialog('deleteDocument', document)}
                    color="error"
                    variant="contained"
                  >
                    Delete Document
                  </Button>
                </FlexBetween>
                <Button
                  sx={{ fontSize: '0.7rem', '@media (max-width:1499.98px)': { fontSize: '0.56rem' } }}
                  disabled={reviewLoading || templateLoading}
                  onClick={() => {
                    setSelectedDocument(document);
                    pdfTemplate ? createTemplate() : reviewDocument(false, document)
                  }}
                  color="success"
                  variant="contained"
                >
                  {reviewLoading || templateLoading ? <CircularProgress size={14} style={{ color: '#fff' }} /> : !pdfTemplate ? 'Review Document' : 'Send'}
                </Button>
              </FlexEnd>
            ) : null}
          </FlexBetween>
        </Box>
      );
    }
  };

  let createMultipleDocuments = async (draft: boolean) => {
    let purchasers: any[] = [];
    let options: any[] = [];
    if (selectedMerge[0].type === 'Management') {
      purchasers = lease.landlords;
      options = lease.management.options;
    }
    if (selectedMerge[0].type === 'Listing') {
      purchasers = lease.landlords;
    }
    if (selectedMerge[0].type === 'Tenancy') {
      purchasers = lease.landlords;
    }

    const mergedPdf = await PDFDocument.create();
    let documents = await Promise.all(
      selectedMerge.map(async (document: IMerge) => {
        let autoMergeTemplate = mergeTemplates.find((mergeTemplate: IMerge) => mergeTemplate.name === document.name);

        let autoDocument = await fetch(autoMergeTemplate?.getUrl!).then(async (res) => await res.arrayBuffer());
        const documentPdf = await PDFDocument.load(autoDocument);
        let helveticaFont = await documentPdf.embedFont(StandardFonts.Helvetica);

        let pages = documentPdf.getPages();
        await autoMergeTemplate?.mergeFields.forEach(async (mergeField: any) => {
          let mergePages = pages[mergeField.pageNumber];
          let mergeValue = getMergeValues(
            mergeField,
            project,
            unit,
            null,
            purchasers,
            [],
            [],
            filteredDeal.deposit,
            options,
            null,
            null,
            null,
            null,
            null,
            filteredDeal,
            [],
            null,
            lease
          );

          if (mergeField.key === 'PropertyInsurance') {
            if (lease && lease.management && lease.management.insurance) {
              pages[6].drawLine(drawCheckbox({ x: 93, y: 762 }, { x: 98, y: 767 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
              pages[6].drawLine(drawCheckbox({ x: 90, y: 764 }, { x: 93, y: 762 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
            } else if (lease && lease.management && !lease.management.insurance) {
              pages[6].drawLine(drawCheckbox({ x: 93, y: 709 }, { x: 98, y: 714 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
              pages[6].drawLine(drawCheckbox({ x: 90, y: 711 }, { x: 93, y: 709 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
            }
          }

          if (mergeValue === 'Check') {
            if (mergeField.key === 'PropertyInsurance') {
              if (lease && lease.management && lease.management.insurance) {
                pages[6].drawLine(drawCheckbox({ x: 93, y: 762 }, { x: 98, y: 767 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
                pages[6].drawLine(drawCheckbox({ x: 90, y: 764 }, { x: 93, y: 762 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
              } else if (lease && lease.management && !lease.management.insurance) {
                pages[6].drawLine(drawCheckbox({ x: 93, y: 709 }, { x: 98, y: 714 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
                pages[6].drawLine(drawCheckbox({ x: 90, y: 711 }, { x: 93, y: 709 }, 1, { first: 0, second: 0, third: 0 }, 0.75));
              }
            } else {
              mergePages.drawLine({
                start: { x: mergeField.x + 3, y: mergePages.getHeight() - mergeField.y - 3 },
                end: { x: mergeField.x + 8, y: mergePages.getHeight() - mergeField.y + 4 },
                thickness: 1,
                color: rgb(0, 0, 0),
                opacity: 0.75,
              });
              mergePages.drawLine({
                start: { x: mergeField.x, y: mergePages.getHeight() - mergeField.y + 1 },
                end: { x: mergeField.x + 3, y: mergePages.getHeight() - mergeField.y - 3 },
                thickness: 1,
                color: rgb(0, 0, 0),
                opacity: 0.75,
              });
            }
          } else {
            let checkMediaBox = mergePages.getMediaBox();
            let x = mergeField.x;
            let y = mergePages.getHeight() - mergeField.y - 3;

            if (checkMediaBox && (checkMediaBox.x !== 0 || checkMediaBox.y !== 0)) {
              x = mergeField.x - Math.abs(checkMediaBox.x);
              y = mergePages.getHeight() - mergeField.y - 3 - Math.abs(checkMediaBox.y);
            }

            let textProperties: any = {
              x: x,
              y: y,
              size: mergeField.fontSize,
              font: helveticaFont,
              color: rgb(0, 0, 0),
            };

            if (mergeField.wrap) {
              mergeValue = wrapText(mergeValue, mergeField.wrap * 2 + 75, helveticaFont, 9);
              if (mergeValue.lineHeight) {
                textProperties.lineHeight = mergeValue.lineHeight;
                textProperties.y = textProperties.y + 10;
              }
              mergeValue = mergeValue.text;
            }

            mergePages.drawText(mergeValue, textProperties);
          }
        });

        let copiedPages = await mergedPdf.copyPages(documentPdf, documentPdf.getPageIndices());
        copiedPages.forEach((page: any) => mergedPdf.addPage(page));

        const documentDoc: any = await documentPdf.save();

        let mergeSignFields = autoMergeTemplate?.signFields.map((signFields: any, index: number) => {
          return {
            index: signFields.index,
            key: signFields.key,
            pageNumber: signFields.pageNumber,
            x: Math.round(signFields.x),
            y: Math.round(signFields.y),
          };
        });

        let blob = await new Blob([documentDoc]);
        let file = await new File([blob], `${document.name}.pdf`, { type: 'application/pdf' });

        return {
          file: file,
          name: document.name,
          mergeTemplate: document._id,
          signFields: mergeSignFields,
          isAPS: false,
          type: document.type,
        };
      })
    );

    let leaseType = null;

    if (selectedMerge.some((merge: any) => merge.type === 'Tenancy')) {
      leaseType = 'tenancy';
    } else if (selectedMerge.some((merge: any) => merge.type === 'Listing')) {
      leaseType = 'listing';
    } else if (selectedMerge.some((merge: any) => merge.type === 'Management')) {
      leaseType = 'management';
    } else if (selectedMerge.some((merge: any) => merge.type === 'Leasing')) {
      leaseType = 'leasing';
    }

    createMultipleDocument({
      variables: { project: project._id, deal: filteredDeal._id, documents: documents, draft, leaseId: lease._id, leaseType },
    });
    setDialogType('');
    setDialogOpen(false);
  };

  const handleCloseSuccess = async () => {
    if (dialogType === 'execute') {
      sendToExecutor();
    } else if (dialogType === 'upload') {
      const args = {
        status: 'Completed',
        name: title,
        project: project._id,
        deal: filteredDeal._id,
        isAPS: false,
        type: documentType,
        mergeTemplate: null,
      };
      setDialogOpen(false);
      createDocument({ variables: { record: args, file: file } });
    } else if (dialogType === 'sendEnvelope') {
      handleSendDocument(selectedDocument!);
    } else if (dialogType === 'deleteDocument') {
      removeDocuments(selectedDocument?._id!, selectedDocument?.type!);
    } else {
      if (selectedMerge.length === 1) {
        let purchasers: any[] = [];
        let options: any[] = [];
        if (selectedMerge[0].type === 'Management') {
          purchasers = lease.landlords;
          options = lease.management.options;
        }
        if (selectedMerge[0].type === 'Listing') {
          purchasers = lease.landlords;
        }
        if (selectedMerge[0].name === 'Tenancy Agreement') {
          purchasers = lease.landlords;
        }
        let pdf = await createPdf(
          selectedMerge[0],
          project,
          unit,
          purchasers,
          [],
          [],
          [],
          options,
          null,
          null,
          null,
          null,
          null,
          null,
          false,
          [],
          [],
          false,
          lease
        );

        let sign = selectedMerge[0].signFields.map((sign: any) => {
          return {
            index: sign.index,
            key: sign.key,
            pageNumber: sign.pageNumber,
            x: sign.x,
            y: sign.y,
          };
        });

        let mergeName = selectedMerge[0].name;

        const args = {
          status: 'Prepared',
          name: mergeName,
          project: project._id,
          deal: filteredDeal._id,
          unit: unit._id,
          signFields: sign,
          // signers,
          isAPS: false,
          mergeTemplate: [selectedMerge[0]._id],
          type: selectedMerge[0].type,
        };

        setDialogOpen(false);
        createDocument({ variables: { record: args, file: pdf } });
      } else if (selectedMerge.length > 1) {
        createMultipleDocuments(false);
      }
    }
  };

  const handleCloseSecond = () => {
    createMultipleDocuments(true);
  };

  const handleSendDocument = async (document: IDocuments) => {
    let name = '';

    if (document.type === 'Management') {
      name = 'management';
    } else if (document.type === 'Listing') {
      name = 'listing';
    } else if (document.type === 'Tenancy') {
      name = 'tenancy';
    }

    // sendDocument({
    //   variables: {
    //     documentId: document._id,
    //     dealRealtorId: null,
    //     leaseId: lease._id,
    //     leaseType: name,
    //   },
    // });
    setDialogOpen(false);
  };

  const removeDocuments = async (documentId: string, type: string) => {
    setDialogOpen(false);
    deleteDocument({ variables: { _id: documentId, deleteFile: true, leaseId: lease ? lease._id : null } });
  };

  const dialogBox = () => {
    let removeButton = 'Cancel';
    let secondOption;
    let successButton = 'Yes, I would like to create this agreement';
    let title = 'Please select an agreement you would like to create.';
    if (dialogType === 'execute') {
      successButton = 'Send to Executor';
      title = 'Please select an Executor to send Document';
    }
    if (dialogType === 'upload') {
      successButton = 'Upload Document';
      title = 'Please select a type for this document';
    }
    if (dialogType === 'sendEnvelope') {
      successButton = 'Send Envelope';
      title = 'Are you sure you would like to send this envelope?';
    }
    if (dialogType === 'deleteDocument') {
      successButton = 'Delete/Void Document';
      title = 'Are you sure you would like to delete/void this document?';
    }
    if (dialogType === 'create' && selectedMerge.length > 1) {
      secondOption = 'Create Draft';
      successButton = '';
    }
    return (
      <CustomDialog
        handleClose={() => setDialogOpen(false)}
        handleCloseRemove={() => setDialogOpen(false)}
        handleCloseSuccess={handleCloseSuccess}
        handleCloseSecond={handleCloseSecond}
        secondOption={secondOption}
        open={dialogOpen}
        removeButton={removeButton}
        successButton={successButton}
        dialogContent={dialogContent()}
        dialogTitle={title}
        maxWidth={1000}
      />
    );
  };

  const handleDropdownInput = (event: any) => {
    let selected = mergeTemplates.find((merge: IMerge) => {
      return merge.name === event.target.value;
    });
    if (selected) {
      setSelectedMerge([...selectedMerge, selected]);
    }
  };

  const handleMenuList = () => {
    let listingTemplates: string[] = [];
    let tenancyTemplates: string[] = [];
    let managementTemplates: string[] = [];
    let leasingTemplates: string[] = [];
    if (lease.listing) {
      listingTemplates = mergeTemplates.filter((merge: IMerge) => merge.type === 'Listing').map((merge: IMerge) => merge.name);
    }
    if (lease.management) {
      managementTemplates = mergeTemplates.filter((merge: IMerge) => merge.type === 'Management').map((merge: IMerge) => merge.name);
    }
    if (lease.tenancy) {
      tenancyTemplates = mergeTemplates.filter((merge: IMerge) => merge.type === 'Tenancy').map((merge: IMerge) => merge.name);
    }
    leasingTemplates = mergeTemplates.filter((merge: IMerge) => merge.type === 'Leasing').map((merge: IMerge) => merge.name);

    return [...managementTemplates, ...listingTemplates, ...tenancyTemplates, ...leasingTemplates].sort();
  };

  const dialogContent = () => {
    if (dialogType === 'create') {
      return (
        <Box sx={{ mt: 1 }}>
          <Dropdown
            id={'type'}
            title={'Agreement'}
            menuList={handleMenuList()}
            name={'type'}
            handleSelect={(e: any) => handleDropdownInput(e)}
            value={''}
          />
          {selectedMerge.length ? (
            <List>
              {selectedMerge.map((merge: IMerge, index: number) => {
                return (
                  <ListItem
                    key={index}
                    sx={{ backgroundColor: '#e5e5e5', borderRadius: '8px', mb: 1 }}
                    secondaryAction={
                      <IconButton edge="end" aria-label="delete">
                        <DeleteIcon
                          color="error"
                          onClick={() => setSelectedMerge(selectedMerge.filter((selected: IMerge) => selected._id !== merge._id))}
                        />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={`${index + 1}. ${merge.name}`} />
                  </ListItem>
                );
              })}
            </List>
          ) : null}
        </Box>
      );
    } else if (dialogType === 'execute') {
      return (
        <div>
          <Dropdown
            title={'Executor'}
            menuList={executorList()}
            name={'executors'}
            handleSelect={(e: any) => handleExecutorDropdown(e)}
            value={executor ? executor.name : ''}
          />
        </div>
      );
    } else if (dialogType === 'sendEnvelope' || dialogType === 'deleteDocument') {
      return (
        <div>
          <strong>{selectedDocument?.name!}</strong>
        </div>
      );
    } else if (dialogType === 'upload') {
      return (
        <div>
          <Box sx={{ my: 2 }}>
            <TextInput
              title={'Document Title'}
              id={'title'}
              handleTextInput={(e: any) => setTitle(e.target.value)}
              value={title}
              required={true}
            />
          </Box>
          <Box
            sx={{
              marginBottom: '30px',
              '& .MuiAccordionSummary-content': {
                display: 'block',
              },
            }}
          >
            <Dropdown
              id={'type'}
              title={'Type'}
              menuList={mergeType.filter((merge: string) => merge === 'Listing' || merge === 'Tenancy' || merge === 'Management')}
              name={'type'}
              handleSelect={(e: any) => setDocumentType(e.target.value)}
              value={documentType}
            />
          </Box>
          <div>
            {fileImage ? (
              <div>
                <Grid item xs={12}>
                  <PdfCard file={fileImage} title={title} id={'0'} handleDelete={() => setFileImage(null)} download={true} />
                </Grid>
              </div>
            ) : (
              <Dropzone onDrop={handleDrop} accept="application/pdf">
                {({ getRootProps, getInputProps }) => (
                  <section>
                    <div {...getRootProps({ style })}>
                      <input {...getInputProps()} />
                      <p>Drag and Drop or Upload a Document (.pdf)</p>
                    </div>
                  </section>
                )}
              </Dropzone>
            )}
          </div>
        </div>
      );
    }
  };

  return (
    <div>
      <Box
        sx={{
          '& .MuiAccordion-root': {
            border: '1px solid #000',
            padding: '4px',
          },
          '& .MuiAccordionDetails-root': {
            display: 'block',
          },
          my: 2,
        }}
      >
        {dialogBox()}
        {lease ? (
          createDocumentLoading || sendDocumentLoading || deleteDocumentLoading || loading ? (
            <LoadingWrapper title="Loading..." modal={false} />
          ) : (
            <>
              {lease.documents.length > 0 ? (
                <Box>
                  {lease.documents.map((document: IDocuments, index: number) => {
                    return (
                      <React.Fragment key={index}>
                        <Accordion
                          sx={{
                            ...setBackgroundColor(document.status),
                            mb: 1,
                          }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                            <div>
                              <h3>{document.name}</h3>
                              <p>
                                Status: <strong>{document.status}</strong>
                              </p>
                            </div>
                          </AccordionSummary>
                          <AccordionDetails>{accordianContent(document)}</AccordionDetails>
                        </Accordion>
                      </React.Fragment>
                    );
                  })}
                </Box>
              ) : (
                <Box>
                  <em>There are currently no documents</em>
                </Box>
              )}
              {accessAllowed(user, project._id, 'editDeals') ? (
                <FlexBetween sx={{ mt: 2 }}>
                  <FlexBetween>
                    <div>
                      <Button onClick={() => openDialog('create')} color="primary" variant="contained">
                        Create a Document
                      </Button>
                    </div>
                  </FlexBetween>
                  <FlexBetween>
                    <div>
                      <Button onClick={() => openDialog('upload')} color="primary" variant="contained">
                        Upload a Document
                      </Button>
                    </div>
                  </FlexBetween>
                </FlexBetween>
              ) : null}
            </>
          )
        ) : (
          <div>
            <em>There are no documents.</em>
          </div>
        )}
      </Box>
    </div>
  );
};


const CREATEDOCUMENT = gql`
  mutation documentCreateOne($record: CreateOneDocumentInput, $file: Upload!) {
    documentCreateOne(record: $record, file: $file) {
      _id
      name
      project {
        _id
      }
      type
      getUrl
      status
      isAPS
      dsEnvelopeId
      mergeTemplate
    }
  }
`;

const DELETEDOCUMENT = gql`
  mutation documentDeleteById($_id: MongoID!, $deleteFile: Boolean!, $leaseId: MongoID) {
    documentDeleteById(_id: $_id, deleteFile: $deleteFile, leaseId: $leaseId) {
      name
      status
      _id
    }
  }
`;

const SENDDOCUMENT = gql`
  mutation documentCreateEnvelope($documentId: MongoID!, $dealRealtorId: MongoID, $templateId: String) {
    documentCreateEnvelope(documentId: $documentId, dealRealtorId: $dealRealtorId, templateId: $templateId) {
      name
      _id
      status
      dsEnvelopeId
    }
  }
`;

const SENDDOCUMENTTOEXECUTOR = gql`
  mutation documentSentToExecutor($documentId: MongoID!, $executor: executorData) {
    documentSendToExecutor(documentId: $documentId, executor: $executor) {
      _id
      status
      name
    }
  }
`;

const GETDOCUMENT = gql`
  query getDocument($project: MongoID!, $document: MongoID!) {
    getDocument(project: $project, document: $document) {
      url
      createdAt
      status
      completedAt
      signers {
        name
        completedAt
        status
        openedAt
      }
    }
  }
`;

const CREATEMULTIPLEDOCUMENT = gql`
  mutation createEnvelopeMultipleDocuments(
    $project: MongoID!
    $deal: MongoID!
    $documents: [NewDocumentInput]
    $draft: Boolean
    $leaseId: MongoID
    $leaseType: String
  ) {
    createEnvelopeMultipleDocuments(
      project: $project
      deal: $deal
      documents: $documents
      draft: $draft
      leaseId: $leaseId
      leaseType: $leaseType
    ) {
      _id
      name
      project {
        _id
      }
      type
      getUrl
      status
      isAPS
      dsEnvelopeId
    }
  }
`;

const RESENDDOCUMENT = gql`
  query resendEnvelope($_id: MongoID!) {
    resendEnvelope(_id: $_id)
  }
`;

const SAVEPDF = gql`
  mutation savePdf(
    $project: MongoID!
    $unit: MongoID!
    $file: Upload
    $document: MongoID
    $purchaserIds: [MongoID]
    $realtor: MongoID
    $mergeTemplate: MongoID
    $floorplan: MongoID
    $additional: [MongoID]
    $executor: Boolean
    $lease: MongoID
    $aps: Boolean!
  ) {
    savePdf(
      project: $project
      unit: $unit
      file: $file
      document: $document
      purchaserIds: $purchaserIds
      realtor: $realtor
      mergeTemplate: $mergeTemplate
      floorplan: $floorplan
      additional: $additional
      executor: $executor
      lease: $lease
      aps: $aps
    ) {
      templateId
      createUrl
    }
  }
`;

export default Documents;
